/**
 * Splitwise exports one NET effect per member (`paid − share`); paid and
 * share are not separable from it. We rebuild a pair that has the same net
 * for everybody, which is all the balances depend on:
 *
 *   share_i = max(0, −net_i)                  (debtors consumed their debt)
 *   residual = cost − Σ share_i               (what the payers consumed)
 *   residual is spread equally over the positive-net members
 *   paid_i   = net_i + share_i                (≥ 0, and Σ paid = cost)
 *
 * Everything is computed from nets already rounded to the row currency, so
 * a 0-decimal currency (COP, JPY, …) never yields an amount the expense
 * service would round away underneath us.
 */
import {
  getCurrencyDecimals,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';

import { settleDrift } from '../expense/ConversionService';

export interface ReconstructedRow {
  payers: Array<{ column: number; amount: number }>;
  splits: Array<{ column: number; amount: number }>;
}

export interface ReconstructedPayment {
  fromColumn: number;
  toColumn: number;
  amount: number;
}

/**
 * Rounds each net to the row currency and folds the rounding residual back
 * in, so the rounded nets still add up to what the file said (zero for a
 * balanced row) instead of drifting by a sub-unit per member.
 */
export function roundEffectsToCurrency(
  effects: number[],
  currency: string
): number[] {
  const nets = effects.map(n => roundToCurrencyPrecision(n, currency));
  const total = roundToCurrencyPrecision(
    effects.reduce((s, n) => s + n, 0),
    currency
  );
  const drift = roundToCurrencyPrecision(
    total - nets.reduce((s, n) => s + n, 0),
    currency
  );
  if (drift === 0) return nets;
  // The largest net absorbs the sub-unit rounding drift: on a balanced row
  // that is a payer, so nobody ends up owing a unit they never agreed to.
  let target = 0;
  for (let i = 1; i < nets.length; i += 1)
    if (nets[i] > nets[target]) target = i;
  nets[target] = roundToCurrencyPrecision(nets[target] + drift, currency);
  return nets;
}

export function reconstructSplitwiseRow(
  cost: number,
  effects: number[],
  currency: string
): ReconstructedRow {
  const total = roundToCurrencyPrecision(cost, currency);
  const nets = roundEffectsToCurrency(effects, currency);
  const shares = nets.map(n => (n < 0 ? -n : 0));

  const positives: number[] = [];
  nets.forEach((n, i) => {
    if (n > 0) positives.push(i);
  });

  const residual = roundToCurrencyPrecision(
    total - shares.reduce((s, n) => s + n, 0),
    currency
  );

  if (positives.length > 0 && residual > 0) {
    const even = roundToCurrencyPrecision(
      residual / positives.length,
      currency
    );
    // `even` can round the average up, and from four payers upward n−1 copies
    // of it then exceed the residual, leaving the last payer a negative share;
    // stepping down one minor unit (the floor of the average) never can.
    const base =
      roundToCurrencyPrecision(even * (positives.length - 1), currency) >
      residual
        ? roundToCurrencyPrecision(
            even - 10 ** -getCurrencyDecimals(currency),
            currency
          )
        : even;
    settleDrift(
      positives.map(column => ({ column, amount: base })),
      residual,
      currency
    ).forEach(({ column, amount }) => {
      shares[column] = amount;
    });
  }

  const paid = nets.map((net, i) =>
    roundToCurrencyPrecision(net + shares[i], currency)
  );

  return {
    payers: paid
      .map((amount, column) => ({ column, amount }))
      .filter(p => p.amount > 0),
    splits: shares
      .map((amount, column) => ({ column, amount }))
      .filter(s => s.amount > 0),
  };
}

/**
 * A payment row moves `cost` from the one positive net to the one negative
 * net. Malformed input (the parser marks those `payment-shape` and skips
 * them) yields a `-1` column rather than throwing.
 */
export function reconstructSplitwisePayment(
  cost: number,
  effects: number[],
  currency: string
): ReconstructedPayment {
  const nets = roundEffectsToCurrency(effects, currency);
  return {
    fromColumn: nets.findIndex(n => n > 0),
    toColumn: nets.findIndex(n => n < 0),
    amount: roundToCurrencyPrecision(cost, currency),
  };
}
